import React, { useState, useEffect, useCallback } from "react";
import "./AdvancedSettingsModal.css";
import ModalToggle from "./ModalToggle";
import UnsavedChangesModal from "./UnsavedChangesModal";
import config from "./config";
import logger from './utils/logger';

const AdvancedSettingsModal = ({
  isOpen,
  onClose,
  orgId,
  organizationName,
  userRole,
  onSettingsUpdated,
}) => {
  const [settings, setSettings] = useState({
    autoProvisionCredentials: true,
    receiveCredentialNotifications: true,
    enableDevStreamsForAllRoles: false,
  });
  const [originalSettings, setOriginalSettings] = useState({
    autoProvisionCredentials: true,
    receiveCredentialNotifications: true,
    enableDevStreamsForAllRoles: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(
          orgId
        )}/settings`,
        {
          method: "GET",
          headers: {
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.statusText}`);
      }

      const data = await response.json();
      setSettings(data.settings);
      setOriginalSettings(data.settings);
    } catch (err) {
      logger.error("Error fetching organization settings:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (isOpen && orgId) {
      fetchSettings();
    }
  }, [isOpen, orgId, fetchSettings]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen && !showUnsavedModal) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showUnsavedModal, hasChanges]);

  const handleToggleChange = (settingKey, newValue) => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        [settingKey]: newValue,
      };

      // Check if settings match original
      const hasActualChanges =
        newSettings.autoProvisionCredentials !==
          originalSettings.autoProvisionCredentials ||
        newSettings.receiveCredentialNotifications !==
          originalSettings.receiveCredentialNotifications ||
        newSettings.enableDevStreamsForAllRoles !==
          originalSettings.enableDevStreamsForAllRoles;

      setHasChanges(hasActualChanges);
      return newSettings;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(
          orgId
        )}/settings`,
        {
          method: "PUT",
          headers: {
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ settings }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update settings: ${response.statusText}`);
      }

      const data = await response.json();
      setSettings(data.settings);
      setOriginalSettings(data.settings);
      setHasChanges(false);

      // Notify parent of settings update
      if (onSettingsUpdated) {
        onSettingsUpdated(data.settings);
      }

      onClose(); // Close modal on successful save
    } catch (err) {
      logger.error("Error updating organization settings:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
      return;
    }
    setHasChanges(false);
    onClose();
  };

  const handleConfirmClose = () => {
    setShowUnsavedModal(false);
    setHasChanges(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedModal(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="advanced-settings-overlay" onClick={handleClose}>
        <div
          className="advanced-settings-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>

          <h2>Advanced Settings for {organizationName} Organization</h2>
          <p className="subtitle">
            Manage organization-level preferences for credential management
          </p>

          {error && <div className="error-state">{error}</div>}

          {isLoading ? (
            <div className="loading-state">Loading settings...</div>
          ) : (
            <>
              <div className="settings-section">
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Auto-Provision Credentials</h3>
                    <p>
                      Automatically provision newly downloaded credentials to
                      all devices. When disabled, credentials must be manually
                      added to devices.
                    </p>
                  </div>
                  <div className="setting-control">
                    <ModalToggle
                      isChecked={settings.autoProvisionCredentials}
                      onToggle={(newValue) =>
                        handleToggleChange("autoProvisionCredentials", newValue)
                      }
                      label=""
                    />
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Receive Credential Download Notifications</h3>
                    <p>
                      Show a notification when credentials are downloaded from
                      the credential provider.
                    </p>
                  </div>
                  <div className="setting-control">
                    <ModalToggle
                      isChecked={settings.receiveCredentialNotifications}
                      onToggle={(newValue) =>
                        handleToggleChange(
                          "receiveCredentialNotifications",
                          newValue
                        )
                      }
                      label=""
                    />
                  </div>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Enable Developer Streams For All Organization Roles</h3>
                    <p>
                      Allow all users within the organization to access
                      Developer Event Streams, regardless of their role. When
                      disabled, only Full Admins can view Developer Streams.
                    </p>
                    {userRole !== "Full Admin" && (
                      <p className="setting-restriction-notice">
                        Only Full Admins can modify this setting.
                      </p>
                    )}
                  </div>
                  <div className="setting-control">
                    <ModalToggle
                      isChecked={settings.enableDevStreamsForAllRoles}
                      onToggle={(newValue) =>
                        handleToggleChange(
                          "enableDevStreamsForAllRoles",
                          newValue
                        )
                      }
                      label=""
                      disabled={userRole !== "Full Admin"}
                    />
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button className="btn btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};

export default AdvancedSettingsModal;
